/**
 * @component ry-logo
 * @category  display
 * @summary   Brand logo (ry-ui-only). Reads CSS tokens (`--ry-logo`, `--ry-logo-light`, `--ry-logo-dark`) for the icon and `--ry-wordmark` (`--ry-wordmark-light`, `--ry-wordmark-dark`) for the optional wordmark. Both tokens are CSS `url(...)` values (typically inline SVG data URIs) so the wordmark renders without any font dependency. Auto-switches between light/dark variants based on the active color-scheme; `[variant]` forces one. Pure display.
 *
 * @attribute size         string?   default="28"   Icon height in px. Width is determined by the SVG/PNG aspect via `background-size: contain`.
 * @attribute inline-text  boolean   default=false   Render the wordmark text (from `--ry-wordmark` token) next to the icon. Without this, only the icon shows.
 * @attribute variant      'auto' | 'light' | 'dark' ?   default="auto"   Force a specific variant. `auto` uses the `light-dark()`-aware composite token; `light` / `dark` pick the static variant.
 * @attribute alt          string?   default="Logo"   Accessible label applied to the icon span via `aria-label` (the icon span has `role="img"`).
 *
 * @content   None — `<ry-logo>` is configured entirely via attributes. innerHTML is replaced at every render.
 *
 * @dom (icon only)
 *   ry-logo[size][variant][alt]
 *     > span.ry-logo__icon   [data-ry-target="icon"]   role="img"   aria-label=alt   style="background-image: var(--ry-logo)..."
 *
 * @dom (with [inline-text])
 *   ry-logo[inline-text]
 *     > span.ry-logo__icon       [data-ry-target="icon"]
 *     > span.ry-logo__wordmark   [data-ry-target="wordmark"]   style="background-image: var(--ry-wordmark)..."
 *
 * @gotcha  ry-ui ships generic "Acme" placeholder SVGs as defaults for `--ry-logo` and `--ry-wordmark`. Consumers should override these tokens in their design system before shipping; without an override every app would look like Acme.
 *
 * @gotcha  `[size]` accepts only a number-or-string of digits — it's appended with `px`. Don't pass `"2rem"` or other CSS lengths; they'd render as `"2rempx"`.
 *
 * @gotcha  The wordmark token is a CSS `url(...)` value (inline SVG data URI by default). To change the wordmark, set `--ry-wordmark` to a new `url(...)` — there is no automatic text-to-paths conversion at runtime, so the SVG must be authored ahead of time.
 *
 * @gotcha  Live attribute updates (size, variant, alt, inline-text) all trigger a full re-render via `observedAttributes` + `attributeChangedCallback`. The icon span identity changes on each render — consumers shouldn't cache references.
 *
 * @example basic
 * ```html
 * <ry-logo></ry-logo>
 * ```
 *
 * @example sized
 * ```html
 * <ry-logo size="48"></ry-logo>
 * ```
 *
 * @example with-wordmark
 * ```html
 * <ry-logo inline-text></ry-logo>
 * ```
 *
 * @example forced-variant
 * ```html
 * <ry-logo variant="dark"></ry-logo>
 * ```
 *
 * @example custom-alt
 * ```html
 * <ry-logo alt="Acme Corp"></ry-logo>
 * ```
 *
 * @tutorial mount-renders-icon
 * ```js
 * const el = mount('<ry-logo></ry-logo>');
 * const icon = el.querySelector('[data-ry-target="icon"]');
 * await expectEqual(!!icon, true, 'icon slot rendered');
 * await expectAttr(icon, 'role', 'img');
 * await expectAttr(icon, 'aria-label', 'Logo');
 * ```
 *
 * @tutorial size-applies-as-inline-px
 * ```js
 * const el = mount('<ry-logo size="48"></ry-logo>');
 * const icon = el.querySelector('[data-ry-target="icon"]');
 * await expectEqual(icon.style.height, '48px', 'size sets inline height');
 * await expectEqual(icon.style.width, '48px');
 * ```
 *
 * @tutorial inline-text-renders-wordmark-slot
 * ```js
 * const el = mount('<ry-logo inline-text></ry-logo>');
 * await expectEqual(!!el.querySelector('[data-ry-target="wordmark"]'), true);
 * ```
 *
 * @tutorial no-inline-text-no-wordmark
 * ```js
 * // Wrong assumption: the wordmark always renders. It's opt-in via [inline-text].
 * const el = mount('<ry-logo></ry-logo>');
 * await expectEqual(el.querySelector('[data-ry-target="wordmark"]'), null);
 * ```
 *
 * @tutorial alt-overrides-aria-label
 * ```js
 * const el = mount('<ry-logo alt="Acme Corp"></ry-logo>');
 * const icon = el.querySelector('[data-ry-target="icon"]');
 * await expectAttr(icon, 'aria-label', 'Acme Corp');
 * ```
 *
 * @tutorial variant-attribute-survives
 * ```js
 * const el = mount('<ry-logo variant="dark"></ry-logo>');
 * await expectAttr(el, 'variant', 'dark');
 * ```
 *
 * @see ry-logo-bar
 * @see ry-image
 */

import { RyElement } from '../core/ry-element.js';

export class RyLogo extends RyElement {
  static get observedAttributes(): string[] {
    return ['size', 'inline-text', 'variant', 'alt'];
  }

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (this.hasAttribute('data-ry-initialized')) this.#render();
  }

  #render(): void {
    const size = this.getAttribute('size') ?? '28';
    const variant = this.getAttribute('variant') ?? 'auto';
    const alt = this.getAttribute('alt') ?? 'Logo';
    const showText = this.hasAttribute('inline-text');

    // Pick the right token. The exporter emits:
    //   --ry-logo-light, --ry-logo-dark, --ry-logo (light-dark() composite)
    let iconVar = 'var(--ry-logo)';
    let wordVar = 'var(--ry-wordmark)';
    if (variant === 'light') {
      iconVar = 'var(--ry-logo-light, var(--ry-logo))';
      wordVar = 'var(--ry-wordmark-light, var(--ry-wordmark))';
    }
    if (variant === 'dark') {
      iconVar = 'var(--ry-logo-dark, var(--ry-logo))';
      wordVar = 'var(--ry-wordmark-dark, var(--ry-wordmark))';
    }

    // background-image is the cleanest path — fills the height we set, the
    // URL is honored from the CSS token (which the exporter writes as data:
    // URL or /assets/ path). Width is auto-derived from contain sizing.
    // The wordmark uses the same background-image strategy so it can ship as
    // an inline SVG (paths) and not depend on any system font.
    this.innerHTML = `
      <span data-ry-target="icon" class="ry-logo__icon" style="
        display: inline-block;
        height: ${size}px;
        width: ${size}px;
        background-image: ${iconVar};
        background-repeat: no-repeat;
        background-position: center left;
        background-size: contain;
        vertical-align: middle;
      " role="img" aria-label="${alt}"></span>${showText ? `<span data-ry-target="wordmark" class="ry-logo__wordmark" style="
        display: inline-block;
        height: ${size}px;
        width: ${Math.round(Number(size) * 3.6)}px;
        background-image: ${wordVar};
        background-repeat: no-repeat;
        background-position: center left;
        background-size: contain;
        vertical-align: middle;
      "></span>` : ''}
    `;
  }
}

customElements.define('ry-logo', RyLogo);
